import React, { useEffect, useMemo, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import { formatBytes } from "aldoo-ra/Admin/KPI/format"
import {
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline"
import { ListOrderFiles, GetFileUrl } from "./api"

// Per-order file browser. Files load from the DB (order.meta.files); a download
// link is presigned lazily, only when the admin clicks one file's name.
const FilesDialog = ({ orderID, onClose }) => {
  const [state, setState] = useState({ loading: true, files: [], error: null, status: null })
  const [busy, setBusy] = useState(null)
  const [search, setSearch] = useState("")
  const [sort, setSort] = useState({ key: "name", dir: 1 })

  useEffect(() => {
    if (!orderID) return
    let alive = true
    setState({ loading: true, files: [], error: null, status: null })
    setSearch("")
    setSort({ key: "name", dir: 1 })
    ListOrderFiles(orderID)
      .then((res) => {
        if (!alive) return
        if (!res || res.error) return setState({ loading: false, files: [], error: res?.error || "load_failed", status: null })
        setState({ loading: false, files: res.files || [], error: null, status: res.status })
      })
      .catch(() => alive && setState({ loading: false, files: [], error: "load_failed", status: null }))
    return () => {
      alive = false
    }
  }, [orderID])

  const download = async (name) => {
    setBusy(name)
    try {
      const res = await GetFileUrl(orderID, name)
      if (res?.url) window.open(res.url, "_blank", "noopener")
    } finally {
      setBusy(null)
    }
  }

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }))

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    const filtered = term
      ? state.files.filter((f) => f.name.toLowerCase().includes(term))
      : state.files
    const sorted = [...filtered].sort((a, b) => {
      if (sort.key === "size") return (a.size - b.size) * sort.dir
      return a.name.localeCompare(b.name) * sort.dir
    })
    return sorted
  }, [state.files, search, sort])

  const SortHeader = ({ label, k, className }) => (
    <th className={`py-2 cursor-pointer select-none ${className || ""}`} onClick={() => toggleSort(k)}>
      <div className={`flex items-center gap-1 ${k === "size" ? "justify-end" : ""}`}>
        {label}
        {sort.key === k ? (
          sort.dir === 1 ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />
        ) : (
          <ChevronUpIcon className="w-3.5 h-3.5 opacity-30" />
        )}
      </div>
    </th>
  )

  return (
    <Modal
      isOpen={!!orderID}
      onClose={onClose}
      closeOnClickOutside
      closeOnEscape
      title={`Files · ${orderID || ""}`}
      className="max-w-2xl w-full"
    >
      <div className="p-1 max-h-[60vh] overflow-auto text-admin_text">
        {state.loading ? (
          <Typography variant="admin_body" className="text-admin_text_secondary py-4">
            Loading files…
          </Typography>
        ) : state.error ? (
          <Typography variant="admin_body" className="text-admin_text_secondary py-4">
            Could not load files ({state.error}).
          </Typography>
        ) : state.files.length === 0 ? (
          <Typography variant="admin_body" className="text-admin_text_secondary py-4">
            No files in this order.
          </Typography>
        ) : (
          <>
            {/* Search */}
            <div className="relative mb-3">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-admin_text_secondary" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search files…"
                className="w-full pl-9 pr-3 py-2 border border-admin_bordered rounded-lg text-sm bg-admin_neutral text-admin_text placeholder-admin_text_secondary focus:outline-none focus:border-admin_primary"
              />
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-admin_text_secondary border-b border-admin_bordered">
                  <SortHeader label="Name" k="name" className="pr-2" />
                  <SortHeader label="Size" k="size" className="pl-2 text-right whitespace-nowrap w-32" />
                </tr>
              </thead>
              <tbody>
                {rows.map((f, i) => (
                  <tr key={i} className="border-b border-admin_bordered">
                    <td className="py-2 pr-2 break-all">
                      {f.downloadable ? (
                        <button
                          className="text-admin_primary hover:underline text-left disabled:opacity-50"
                          disabled={busy === f.name}
                          onClick={() => download(f.name)}
                        >
                          {busy === f.name ? `${f.name} …` : f.name}
                        </button>
                      ) : (
                        <span className="text-admin_text_secondary">{f.name}</span>
                      )}
                    </td>
                    <td className="py-2 pl-2 text-right whitespace-nowrap text-admin_text_secondary">
                      {formatBytes(f.size)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {rows.length === 0 && (
              <Typography variant="admin_body" className="text-admin_text_secondary py-4">
                No files match "{search}".
              </Typography>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}

export default FilesDialog
